import React, { useEffect, useMemo, useState } from "react";
import { useTableAutoRefresh } from "../../hooks/useTableAutoRefresh";
import { MaterialReactTable } from "material-react-table";
import { sharedTableProps } from "../shared/tableProps";
import {
  Box,
  Button,
  Chip,
  Tooltip,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { BarChart } from "@mui/x-charts/BarChart";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import "../util/tables.css";
import { useDataView } from "../../contexts/data.view.context";
import ShowLoadingBar from "../util/showLoadingBar";
import { ToolbarStat, ToolbarRow } from "../shared/monitoringStyles";
import AppDialog from "../util/appDialog";

// A job averaging more than this many runs/day in the selected window is
// "frequent" - see FREQUENT_HIDE note below.
const FREQUENT_RUNS_PER_DAY_THRESHOLD = 50;

// Date-range presets for the "Runs in window" filter. "custom" pairs with
// the two date pickers instead of a fixed lookback.
// NOTE: the underlying EDS.ScheduleJobRunHistory_A query caps rows PER
// (ClassName, ScheduleId) - see JobRunHistory_PerClassCap_Migration.sql -
// not globally, so every job gets a fair slice regardless of how noisy its
// neighbors are. A very high-frequency job's own per-class cap can still
// mean its effective coverage is shorter than a wide range like "90d"; if
// that's visibly the case, raise the per-class row cap in that migration
// rather than picking a narrower default here.
const RANGE_PRESETS = [
  { value: "1d", label: "24h" },
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
  { value: "all", label: "All" },
  { value: "custom", label: "Custom" },
];
const DEFAULT_RANGE_PRESET = "30d";

// Spells out each Range abbreviation, same "ABBR = Meaning" chip convention
// used by ToolbarLegend on the other Monitoring tabs (T/Y/7D/30D) - this tab
// has its own preset set (24h/7d/30d/90d/All/Custom) so it gets its own
// legend rather than reusing that fixed one.
const RANGE_LEGEND = [
  ["24h", "Last 24 Hours"],
  ["7d", "Last 7 Days"],
  ["30d", "Last 30 Days"],
  ["90d", "Last 90 Days"],
  ["All", "All History"],
  ["Custom", "Custom Date Range"],
];

function RangeLegend() {
  return (
    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
      {RANGE_LEGEND.map(([abbr, full]) => (
        <Chip
          key={abbr}
          size="small"
          variant="outlined"
          label={`${abbr} = ${full}`}
          sx={{
            height: 22,
            fontSize: "0.7rem",
            borderColor: "divider",
            color: "text.secondary",
            "& .MuiChip-label": { px: 1 },
          }}
        />
      ))}
    </Box>
  );
}

const STATUS_FILTERS = [
  { value: "ALL", label: "All" },
  { value: "SUCCESS", label: "Success" },
  { value: "SUCCESS_WITH_ERRORS", label: "With Errors" },
  { value: "FAILED", label: "Failed" },
  { value: "RUNNING", label: "Running" },
];

// Reuses the app's theme palette (see App.js's createTheme - primary/success/
// warning/error/info) instead of MUI's default grey selected-state, so these
// toggles read as part of the same app rather than an unstyled control.
// Status buttons pick up the same color as the STATUS_COLOR chips in the
// table (success/error/info) for a consistent success=green/failed=red/
// running=blue vocabulary throughout the tab; Range has no inherent color
// association, so it stays on the app's primary blue.
const STATUS_TOGGLE_COLOR = {
  ALL: "primary",
  SUCCESS: "success",
  SUCCESS_WITH_ERRORS: "warning",
  FAILED: "error",
  RUNNING: "info",
};

function toggleButtonSx(colorKey = "primary") {
  return {
    px: 1.5,
    py: 0.4,
    fontSize: "0.72rem",
    fontWeight: 600,
    textTransform: "none",
    color: "text.secondary",
    border: "1px solid",
    borderColor: "divider",
    "&.Mui-selected": {
      bgcolor: `${colorKey}.main`,
      color: "#fff",
      borderColor: `${colorKey}.main`,
    },
    "&.Mui-selected:hover": {
      bgcolor: `${colorKey}.dark`,
    },
    "&:hover": {
      bgcolor: "action.hover",
    },
  };
}

/** Resolves a preset (or custom start/end date strings from <input type="date">)
 * into concrete Date bounds used to filter runs by StartedTs. */
function getRangeBounds(preset, customStart, customEnd) {
  const now = new Date();
  if (preset === "custom") {
    const start = customStart ? new Date(`${customStart}T00:00:00`) : new Date(0);
    const end = customEnd ? new Date(`${customEnd}T23:59:59.999`) : now;
    return { start, end };
  }
  const daysByPreset = { "1d": 1, "7d": 7, "30d": 30, "90d": 90 };
  const days = daysByPreset[preset];
  const start = days ? new Date(now.getTime() - days * 24 * 60 * 60 * 1000) : new Date(0);
  return { start, end: now };
}

function rangeLabelFor(preset, customStart, customEnd) {
  if (preset === "custom") {
    if (customStart && customEnd) return `${customStart} to ${customEnd}`;
    if (customStart) return `since ${customStart}`;
    return "Custom";
  }
  return RANGE_PRESETS.find((p) => p.value === preset)?.label || preset;
}

// SUCCESS_WITH_ERRORS: the job completed without throwing (JobRun.run's
// wrapped body returned normally), but it logged its own ERROR/FATAL
// message(s) along the way - previously indistinguishable from a clean
// SUCCESS. Colored amber/warning rather than green or red since it's neither
// a clean pass nor a hard failure. STATUS_LABEL keeps the chip text short;
// the full picture is in the Last Error / Error column via ErrorMessage's
// "Nx: <message>" or "N errors logged (M distinct)..." summary.
const STATUS_COLOR = {
  RUNNING: "info",
  SUCCESS: "success",
  SUCCESS_WITH_ERRORS: "warning",
  FAILED: "error",
};

const STATUS_LABEL = {
  SUCCESS_WITH_ERRORS: "WITH ERRORS",
};

const TERMINAL_STATUSES = new Set(["SUCCESS", "SUCCESS_WITH_ERRORS", "FAILED"]);

const CenteredCell = ({ value, style = {} }) => (
  <span
    style={{
      display: "block",
      textAlign: value == null || value === "" ? "center" : "left",
      ...style,
    }}
  >
    {value == null || value === "" ? "-" : value}
  </span>
);

// StartedTs/CompletedTs/InsertDate all come back from the API as bare SQL
// Server `datetime` strings with no timezone marker at all, e.g.
// "2026-09-02 18:30:00.0" - the app server records these in UTC, which is
// why "Last Run" showed a different clock time than the rest of the UI
// (Eastern). Handing a string like that straight to `new Date(...)` does
// NOT convert it from UTC - browsers parse a bare, zone-less datetime as if
// it were already local time, so every timestamp here was effectively being
// displayed (and sorted/filtered/"is this today"-checked) using its raw UTC
// digits mislabeled as local. Parsing the digits manually and re-anchoring
// them with Date.UTC(...) - rather than trusting the browser's lenient,
// non-ISO string parser - gives a real Date instant that can then be
// correctly rendered in, or compared against, whatever timezone the browser
// is actually in.
const RAW_UTC_TIMESTAMP_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/;

function parseUtcTimestamp(rawValue) {
  if (!rawValue) return null;
  const match = RAW_UTC_TIMESTAMP_PATTERN.exec(String(rawValue));
  if (!match) return null;
  const [, year, month, day, hour, minute, second] = match;
  return new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second)),
  );
}

/** Formats a raw UTC datetime value (see parseUtcTimestamp above) in the
 * browser's own local timezone - Eastern for this team - so "Last Run",
 * "Started", "Completed", and "Last Refreshed" all read in the same
 * timezone instead of the run columns alone showing raw UTC digits. Falls
 * back to the raw value, unmodified, if it doesn't match the expected
 * format rather than hiding a value that just looks unusual. */
function formatLocalDateTime(rawValue) {
  const parsed = parseUtcTimestamp(rawValue);
  if (!parsed) return rawValue == null || rawValue === "" ? null : rawValue;
  return parsed.toLocaleString("en-US", {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// DurationMs is a plain integer column - format it the way someone
// scanning a list of runs actually wants to read it (ms below 1s, otherwise
// seconds/minutes), rather than showing raw milliseconds for a 40-minute job.
function formatDuration(durationMs) {
  if (durationMs == null || Number.isNaN(Number(durationMs))) return "-";
  const ms = Number(durationMs);
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remSeconds}s`;
}

// Metrics is stored as a JSON string (whatever the job passed to
// run.record(key, value)) - parse it defensively since it's free-form and
// job-controlled, not a fixed schema.
function parseMetrics(metricsJson) {
  if (!metricsJson) return {};
  try {
    const parsed = JSON.parse(metricsJson);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function StatusChip({ status }) {
  if (!status) return <CenteredCell value={null} />;
  return (
    <Chip
      size="small"
      label={STATUS_LABEL[status] || status}
      color={STATUS_COLOR[status] || "default"}
      variant={status === "RUNNING" ? "outlined" : "filled"}
      sx={{ fontWeight: 700, fontSize: "0.7rem", height: 22 }}
    />
  );
}

function successRateColor(rate) {
  if (rate == null) return "text.secondary";
  if (rate >= 95) return "success.main";
  if (rate >= 80) return "warning.main";
  return "error.main";
}

// The raw table has two lifecycle rows per run - a RUNNING row written at
// start and a SUCCESS/FAILED row written at finish (that's intentional, see
// JOB_TELEMETRY_SPEC.md; JobRunService inserts rather than updates in place).
// Both rows share the same ClassName/ScheduleId/StartedTs, so that triple is
// used here as the run's identity: collapse each pair down to one logical
// run, preferring the terminal (SUCCESS/FAILED) row's data - CompletedTs,
// DurationMs, error info - when one exists, and falling back to the RUNNING
// row when a run hasn't finished yet.
function collapseRunsByStartedTs(rows) {
  const byKey = new Map();
  for (const row of rows) {
    const key = `${row.ClassName}||${row.ScheduleId}||${row.StartedTs}`;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, row);
      continue;
    }
    const existingIsTerminal = TERMINAL_STATUSES.has(existing.Status);
    const rowIsTerminal = TERMINAL_STATUSES.has(row.Status);
    if (!existingIsTerminal && rowIsTerminal) {
      byKey.set(key, row);
    }
  }
  return Array.from(byKey.values());
}

// One line per distinct cron job - grouped by ClassName + ScheduleId, NOT
// ClassName alone. Several classes (StoredProcedureRunner is the clearest
// example) are shared infrastructure invoked with a different ScheduleId
// per actual cron entry (each -sp=... target is its own scheduled job with
// its own success/failure history) - grouping by ClassName only would blend
// all of those distinct jobs' stats into one misleading row.
function summarizeByClass(runsInRange) {
  const byKey = new Map();
  for (const run of runsInRange) {
    const key = `${run.ClassName || "(unknown)"}||${run.ScheduleId || ""}`;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(run);
  }

  const summaries = [];
  for (const groupRuns of byKey.values()) {
    const sorted = [...groupRuns].sort(
      (a, b) => (parseUtcTimestamp(b.StartedTs)?.getTime() || 0) - (parseUtcTimestamp(a.StartedTs)?.getTime() || 0),
    );
    const last = sorted[0];
    const completed = sorted.filter((r) => TERMINAL_STATUSES.has(r.Status));
    const successCount = sorted.filter((r) => r.Status === "SUCCESS").length;
    const withErrorsCount = sorted.filter((r) => r.Status === "SUCCESS_WITH_ERRORS").length;
    const failedCount = sorted.filter((r) => r.Status === "FAILED").length;
    const runningCount = sorted.filter((r) => r.Status === "RUNNING").length;
    const durationsMs = completed.map((r) => Number(r.DurationMs)).filter((d) => !Number.isNaN(d));
    const avgDurationMs = durationsMs.length
      ? durationsMs.reduce((sum, d) => sum + d, 0) / durationsMs.length
      : null;
    // SUCCESS_WITH_ERRORS counts as "completed" for the denominator (it's not
    // a RUNNING/in-flight run) but not as a clean success in the numerator -
    // this is a "how often does it run clean" rate, not just "how often does
    // it avoid throwing."
    const successRate = completed.length ? (successCount / completed.length) * 100 : null;

    summaries.push({
      ClassName: last.ClassName,
      ScheduleId: last.ScheduleId,
      EnvironName: last.EnvironName,
      totalRuns: sorted.length,
      successCount,
      withErrorsCount,
      failedCount,
      runningCount,
      successRate,
      avgDurationMs,
      lastStatus: last.Status,
      lastStartedTs: last.StartedTs,
      lastCompletedTs: last.CompletedTs,
      lastDurationMs: last.DurationMs,
      lastErrorMessage: last.ErrorMessage,
      lastErrorDetail: last.ErrorDetail,
      lastHostName: last.HostName,
      lastLogFileName: last.LogFileName,
    });
  }

  // Sort the array itself by ClassName then ScheduleId - rather than relying
  // on MaterialReactTable's own initial sorting state to do this - so every
  // ScheduleId sharing one class (several DownloadProcessor targets, say)
  // always sits together in the UI regardless of the table's own sort state
  // (which only seeds once on mount and can go stale across a hot reload).
  // The table's "Schedule Id" column still carries a matching sortingFn (see
  // getClassSummaryColumns) so clicking that header reverses this same
  // class-then-schedule grouping instead of switching to a plain
  // alphabetical ScheduleId sort.
  return summaries.sort((a, b) => {
    const classA = (a.ClassName || "").toLowerCase();
    const classB = (b.ClassName || "").toLowerCase();
    if (classA !== classB) return classA < classB ? -1 : 1;
    const scheduleA = (a.ScheduleId || "").toLowerCase();
    const scheduleB = (b.ScheduleId || "").toLowerCase();
    return scheduleA < scheduleB ? -1 : scheduleA > scheduleB ? 1 : 0;
  });
}

// ---------------------------------------------------------------------------
// Analytics popup - overall, cross-job signal (which jobs are running longer
// than usual, is the failure rate trending up, which errors are new) rather
// than any single job's history. Deliberately computed from every collapsed
// run (`allRuns`, not the main table's range/status-filtered rows) so the
// popup always answers "what's changed recently" over its own fixed windows,
// regardless of whatever the main table's filters happen to be set to.
// ---------------------------------------------------------------------------

// A job needs at least this many completed runs before its own average
// means anything - otherwise "50% slower than usual" really means "50%
// slower than the one other time it ran."
const MIN_RUNS_FOR_DURATION_BASELINE = 3;
// The latest run must be at least this many times its own historical
// average to get called out - short jobs wobble by seconds for all kinds
// of harmless reasons, this keeps the list to genuinely unusual runs.
const DURATION_OUTLIER_RATIO = 1.5;
const DURATION_CHART_MAX_ITEMS = 6;
const TREND_WINDOW_DAYS = 7;
// A FAILED/SUCCESS_WITH_ERRORS message with zero matches anywhere in the
// baseline window is "new." One that's on record but firing well above its
// own historical rate is "spiking." Both are worth a human looking at;
// a message that's always fired at roughly this rate is neither.
const RECENT_ERROR_WINDOW_HOURS = 24;
const ERROR_BASELINE_WINDOW_DAYS = 30;
const ERROR_SPIKE_RATIO = 3;
const NEW_ERRORS_MAX_ITEMS = 6;

function durationMinutes(ms) {
  return ms / 60000;
}

// Groups completed runs by ClassName+ScheduleId and flags any whose most
// recent run took much longer than that same job's own historical average.
function computeDurationOutliers(runs) {
  const byKey = new Map();
  for (const run of runs) {
    if (!TERMINAL_STATUSES.has(run.Status)) continue;
    const durationMs = Number(run.DurationMs);
    const startedTs = parseUtcTimestamp(run.StartedTs);
    if (!Number.isFinite(durationMs) || !startedTs) continue;
    const key = `${run.ClassName || "(unknown)"}||${run.ScheduleId || ""}`;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push({ durationMs, startedTs, className: run.ClassName, scheduleId: run.ScheduleId });
  }

  const outliers = [];
  for (const runsForKey of byKey.values()) {
    if (runsForKey.length < MIN_RUNS_FOR_DURATION_BASELINE) continue;
    const [latest, ...history] = [...runsForKey].sort((a, b) => b.startedTs - a.startedTs);
    const baselineAvgMs = history.reduce((sum, r) => sum + r.durationMs, 0) / history.length;
    if (baselineAvgMs <= 0) continue;
    const ratio = latest.durationMs / baselineAvgMs;
    if (ratio >= DURATION_OUTLIER_RATIO) {
      outliers.push({
        className: latest.className,
        scheduleId: latest.scheduleId,
        baselineAvgMs,
        latestMs: latest.durationMs,
        ratio,
      });
    }
  }
  return outliers.sort((a, b) => b.ratio - a.ratio);
}

// Daily Success / With Errors / Failed counts for the last TREND_WINDOW_DAYS
// calendar days (local time, oldest first) - a quick "is this getting
// worse" read that's independent of the main table's own range filter.
function computeDailyTrend(runs) {
  const days = [];
  const now = new Date();
  for (let i = TREND_WINDOW_DAYS - 1; i >= 0; i -= 1) {
    const d = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
    days.push({
      key: d.toDateString(),
      label: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      successCount: 0,
      withErrorsCount: 0,
      failedCount: 0,
    });
  }
  const byDay = new Map(days.map((d) => [d.key, d]));
  const earliestTs = new Date(days[0].key).getTime();

  for (const run of runs) {
    if (!TERMINAL_STATUSES.has(run.Status)) continue;
    const startedTs = parseUtcTimestamp(run.StartedTs);
    if (!startedTs || startedTs.getTime() < earliestTs) continue;
    const bucket = byDay.get(startedTs.toDateString());
    if (!bucket) continue;
    if (run.Status === "SUCCESS") bucket.successCount += 1;
    else if (run.Status === "SUCCESS_WITH_ERRORS") bucket.withErrorsCount += 1;
    else if (run.Status === "FAILED") bucket.failedCount += 1;
  }
  return days;
}

// Trimmed, whitespace-collapsed comparison key - the same "don't let two
// runs of the same failure look like different errors just because a row id
// or timestamp is embedded in the message" idea as JobRun.java's own
// ErrorTracker.normalize(), just applied client-side.
function normalizeErrorMessage(message) {
  if (!message) return "(no message)";
  return message.trim().replace(/\s+/g, " ").slice(0, 200);
}

// Diffs error messages seen in the last RECENT_ERROR_WINDOW_HOURS against
// the ERROR_BASELINE_WINDOW_DAYS before that, per class+message. "New"
// means this exact message never appeared in the baseline window at all;
// "spiking" means it did, but at a much lower rate than it's firing now.
function computeNewCriticalErrors(runs) {
  const now = Date.now();
  const recentCutoff = now - RECENT_ERROR_WINDOW_HOURS * 60 * 60 * 1000;
  const baselineCutoff = recentCutoff - ERROR_BASELINE_WINDOW_DAYS * 24 * 60 * 60 * 1000;

  const recentByKey = new Map();
  const baselineCountByKey = new Map();

  for (const run of runs) {
    if (run.Status !== "FAILED" && run.Status !== "SUCCESS_WITH_ERRORS") continue;
    if (!run.ErrorMessage) continue;
    const startedTs = parseUtcTimestamp(run.StartedTs);
    if (!startedTs) continue;
    const ts = startedTs.getTime();
    if (ts < baselineCutoff) continue;
    const key = `${run.ClassName || "(unknown)"}||${normalizeErrorMessage(run.ErrorMessage)}`;
    if (ts >= recentCutoff) {
      if (!recentByKey.has(key)) {
        recentByKey.set(key, {
          className: run.ClassName,
          scheduleId: run.ScheduleId,
          status: run.Status,
          message: run.ErrorMessage,
          detail: run.ErrorDetail,
          firstSeenTs: startedTs,
          count: 0,
        });
      }
      const entry = recentByKey.get(key);
      entry.count += 1;
      if (startedTs < entry.firstSeenTs) entry.firstSeenTs = startedTs;
    } else {
      baselineCountByKey.set(key, (baselineCountByKey.get(key) || 0) + 1);
    }
  }

  const flagged = [];
  for (const [key, entry] of recentByKey.entries()) {
    const baselineCount = baselineCountByKey.get(key) || 0;
    const baselineDailyRate = baselineCount / ERROR_BASELINE_WINDOW_DAYS;
    const recentDailyRate = entry.count / (RECENT_ERROR_WINDOW_HOURS / 24);
    const isNew = baselineCount === 0;
    const isSpiking = !isNew && baselineDailyRate > 0 && recentDailyRate >= baselineDailyRate * ERROR_SPIKE_RATIO;
    if (isNew || isSpiking) flagged.push({ ...entry, isNew, isSpiking, baselineCount });
  }

  // FAILED before SUCCESS_WITH_ERRORS, brand-new before spiking, more
  // occurrences before fewer - surfaces the sharpest signal first.
  return flagged
    .sort((a, b) => {
      if (a.status !== b.status) return a.status === "FAILED" ? -1 : 1;
      if (a.isNew !== b.isNew) return a.isNew ? -1 : 1;
      return b.count - a.count;
    })
    .slice(0, NEW_ERRORS_MAX_ITEMS);
}

function ErrorCell({ value, detail }) {
  if (!value) return <CenteredCell value={null} />;
  return (
    <Tooltip
      title={
        <span style={{ whiteSpace: "pre-line", fontSize: "0.78rem", lineHeight: 1.6 }}>
          {detail || value}
        </span>
      }
      arrow
      placement="right"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "#2c2c2c",
            color: "#fff",
            fontSize: "0.78rem",
            borderRadius: "6px",
            padding: "8px 12px",
            maxWidth: 480,
            maxHeight: 320,
            overflow: "auto",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          },
        },
        arrow: { sx: { color: "#2c2c2c" } },
      }}
    >
      <span
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
          cursor: "help",
          color: "#d32f2f",
        }}
      >
        {value}
      </span>
    </Tooltip>
  );
}

// LogFileName is always populated regardless of status (see JobRun.java's
// resolveLogFileName()) - it's an absolute path, usually too long to show in
// full inline, so the column shows just the basename with the full path
// available on hover/copy.
function logFileBaseName(path) {
  if (!path) return null;
  const parts = path.split(/[/\\]/);
  return parts[parts.length - 1] || path;
}

function LogFileCell({ value }) {
  if (!value) return <CenteredCell value={null} />;
  return (
    <Tooltip title={<span style={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{value}</span>} arrow placement="right">
      <span
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
          cursor: "help",
          fontFamily: "monospace",
          fontSize: "0.75rem",
          color: "text.secondary",
        }}
      >
        {logFileBaseName(value)}
      </span>
    </Tooltip>
  );
}

// Schedule Id is the real row identity here - a class like StoredProcedureRunner
// is shared infrastructure invoked with many different ScheduleIds, so leading
// with ClassName would show the same label on lots of unrelated rows. ClassName
// is still useful context (which code actually ran), just secondary - shown as
// a muted line under the ScheduleId, with a tooltip in case it's truncated.
function ScheduleIdCell({ scheduleId, className }) {
  const showClassName = Boolean(className) && className !== scheduleId;
  return (
    <Tooltip
      title={showClassName ? `Class: ${className}` : ""}
      arrow
      placement="right"
      disableHoverListener={!showClassName}
    >
      <Box sx={{ textAlign: "left", cursor: showClassName ? "help" : "default" }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {scheduleId || "-"}
        </Typography>
        {showClassName && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: "block",
              lineHeight: 1.3,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {className}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

function getClassSummaryColumns(rangeLabel) {
  return [
    {
      accessorKey: "ScheduleId",
      header: "Schedule Id",
      id: "ScheduleId",
      size: 220,
      // Default sort groups by the actual class first (so every ScheduleId
      // sharing one class, e.g. several targets of DownloadProcessor, sit
      // together in the UI) and only falls back to ScheduleId to order
      // rows within the same class.
      sortingFn: (rowA, rowB) => {
        const classA = (rowA.original.ClassName || "").toLowerCase();
        const classB = (rowB.original.ClassName || "").toLowerCase();
        if (classA !== classB) return classA < classB ? -1 : 1;
        const scheduleA = (rowA.original.ScheduleId || "").toLowerCase();
        const scheduleB = (rowB.original.ScheduleId || "").toLowerCase();
        return scheduleA < scheduleB ? -1 : scheduleA > scheduleB ? 1 : 0;
      },
      Cell: ({ row }) => (
        <ScheduleIdCell scheduleId={row.original.ScheduleId} className={row.original.ClassName} />
      ),
    },
    {
      accessorKey: "totalRuns",
      header: `Runs (${rangeLabel})`,
      id: "totalRuns",
      size: 90,
      Cell: ({ cell }) => <CenteredCell value={cell.getValue()} />,
    },
    {
      accessorKey: "successCount",
      header: "Success",
      id: "successCount",
      size: 90,
      Cell: ({ cell }) => (
        <Typography variant="body2" sx={{ textAlign: "center", color: "success.main", fontWeight: 600 }}>
          {cell.getValue()}
        </Typography>
      ),
    },
    {
      accessorKey: "withErrorsCount",
      header: "With Errors",
      id: "withErrorsCount",
      size: 100,
      Cell: ({ cell }) => (
        <Typography
          variant="body2"
          sx={{ textAlign: "center", fontWeight: 600, color: cell.getValue() > 0 ? "warning.main" : "text.secondary" }}
        >
          {cell.getValue()}
        </Typography>
      ),
    },
    {
      accessorKey: "failedCount",
      header: "Failed",
      id: "failedCount",
      size: 90,
      Cell: ({ cell }) => (
        <Typography
          variant="body2"
          sx={{ textAlign: "center", fontWeight: 600, color: cell.getValue() > 0 ? "error.main" : "text.secondary" }}
        >
          {cell.getValue()}
        </Typography>
      ),
    },
    {
      accessorKey: "successRate",
      header: "Success Rate",
      id: "successRate",
      size: 110,
      Cell: ({ cell }) => {
        const rate = cell.getValue();
        return (
          <Typography variant="body2" sx={{ textAlign: "center", fontWeight: 700, color: successRateColor(rate) }}>
            {rate == null ? "-" : `${rate.toFixed(0)}%`}
          </Typography>
        );
      },
    },
    {
      accessorKey: "avgDurationMs",
      header: "Avg Duration",
      id: "avgDurationMs",
      size: 100,
      Cell: ({ cell }) => <CenteredCell value={formatDuration(cell.getValue())} />,
    },
    {
      accessorKey: "lastStatus",
      header: "Last Status",
      id: "lastStatus",
      size: 110,
      Cell: ({ cell }) => (
        <span style={{ display: "flex", justifyContent: "center" }}>
          <StatusChip status={cell.getValue()} />
        </span>
      ),
    },
    {
      accessorKey: "lastStartedTs",
      header: "Last Run",
      id: "lastStartedTs",
      size: 150,
      Cell: ({ cell }) => <CenteredCell value={formatLocalDateTime(cell.getValue())} />,
    },
    {
      accessorKey: "lastErrorMessage",
      header: "Last Error",
      id: "lastErrorMessage",
      size: 220,
      Cell: ({ cell, row }) => (
        <ErrorCell value={cell.getValue()} detail={row.original.lastErrorDetail} />
      ),
    },
  ];
}

// Per-run columns used inside the drill-down dialog for a single class -
// ClassName is dropped since the dialog title already says which class this is.
function getRunColumns() {
  return [
    {
      accessorKey: "ScheduleId",
      header: "Schedule Id",
      id: "ScheduleId",
      size: 190,
      Cell: ({ cell }) => <CenteredCell value={cell.getValue()} style={{ textAlign: "left" }} />,
    },
    {
      accessorKey: "Status",
      header: "Status",
      id: "Status",
      size: 120,
      Cell: ({ cell }) => (
        <span style={{ display: "flex", justifyContent: "center" }}>
          <StatusChip status={cell.getValue()} />
        </span>
      ),
    },
    {
      accessorKey: "StartedTs",
      header: "Started",
      id: "StartedTs",
      size: 170,
      Cell: ({ cell }) => <CenteredCell value={formatLocalDateTime(cell.getValue())} />,
    },
    {
      accessorKey: "CompletedTs",
      header: "Completed",
      id: "CompletedTs",
      size: 170,
      Cell: ({ cell }) => <CenteredCell value={formatLocalDateTime(cell.getValue())} />,
    },
    {
      accessorKey: "DurationMs",
      header: "Duration",
      id: "DurationMs",
      size: 110,
      Cell: ({ cell }) => <CenteredCell value={formatDuration(cell.getValue())} />,
    },
    {
      accessorKey: "HostName",
      header: "Host",
      id: "HostName",
      size: 190,
    },
    {
      accessorKey: "ErrorMessage",
      header: "Error",
      id: "ErrorMessage",
      size: 240,
      Cell: ({ cell, row }) => <ErrorCell value={cell.getValue()} detail={row.original.ErrorDetail} />,
    },
    {
      accessorKey: "LogFileName",
      header: "Log File",
      id: "LogFileName",
      size: 170,
      Cell: ({ cell }) => <LogFileCell value={cell.getValue()} />,
    },
  ];
}

// Drill-down panel for a single run - the raw args it was invoked with,
// whatever metrics it recorded via run.record(...), and (for a FAILED run)
// the full stack trace, none of which fit in the summary row.
function JobRunDetailPanel({ row }) {
  const r = row.original;
  const metrics = parseMetrics(r.Metrics);
  const metricEntries = Object.entries(metrics);

  return (
    <Box sx={{ p: 2, bgcolor: "#FAFBFC" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4, mb: metricEntries.length ? 2 : 0 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Args
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem", mt: 0.5 }}>
            {r.Args || "-"}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Recorded by
          </Typography>
          <Typography variant="body2" sx={{ fontSize: "0.8rem", mt: 0.5 }}>
            {r.InsertBy || "-"}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Run Id
          </Typography>
          <Typography variant="body2" sx={{ fontSize: "0.8rem", mt: 0.5 }}>
            {r.pkJobRun ?? "-"}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Log File
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem", mt: 0.5, wordBreak: "break-all" }}>
            {r.LogFileName || "-"}
          </Typography>
        </Box>
      </Box>

      {metricEntries.length > 0 && (
        <Box sx={{ mb: r.ErrorDetail ? 2 : 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Metrics
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
            {metricEntries.map(([key, value]) => (
              <Chip
                key={key}
                size="small"
                variant="outlined"
                label={`${key}: ${value}`}
                sx={{ fontSize: "0.75rem", borderColor: "divider" }}
              />
            ))}
          </Box>
        </Box>
      )}

      {r.ErrorDetail && (
        <Box>
          <Typography variant="caption" color="error.main" sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Error Detail
          </Typography>
          <Box
            sx={{
              mt: 0.75,
              p: 1.5,
              bgcolor: "#2c2c2c",
              color: "#fff",
              borderRadius: 1,
              fontFamily: "monospace",
              fontSize: "0.75rem",
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
              maxHeight: 260,
              overflow: "auto",
            }}
          >
            {r.ErrorDetail}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// Drill-down dialog: every individual run (RUNNING+terminal rows already
// collapsed into one line each by collapseRunsByStartedTs) for one class,
// most recent first, expandable per row for args/metrics/error detail.
// `runs` is already limited to the outer table's selected date range;
// the dialog has its own Status toggle (defaulting to whatever the outer
// table's status filter was) so "show me last week's failures" carries
// through when you drill into a specific class.
function ClassRunsDialog({ open, onClose, classSummary, runs, rangeLabel, initialStatusFilter }) {
  const runColumns = useMemo(() => getRunColumns(), []);
  const [dialogStatusFilter, setDialogStatusFilter] = useState(initialStatusFilter || "ALL");

  useEffect(() => {
    if (open) setDialogStatusFilter(initialStatusFilter || "ALL");
  }, [open, initialStatusFilter]);

  const filteredRuns = useMemo(
    () => (dialogStatusFilter === "ALL" ? runs : runs.filter((r) => r.Status === dialogStatusFilter)),
    [runs, dialogStatusFilter],
  );

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={
        classSummary
          ? classSummary.ClassName === classSummary.ScheduleId
            ? `${classSummary.ScheduleId} — ${rangeLabel}`
            : `${classSummary.ScheduleId} (${classSummary.ClassName}) — ${rangeLabel}`
          : ""
      }
      maxWidth="xl"
      fullWidth
    >
      {classSummary && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {filteredRuns.length} of {runs.length} run{runs.length === 1 ? "" : "s"} shown
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={dialogStatusFilter}
              onChange={(e, val) => val && setDialogStatusFilter(val)}
              sx={{ bgcolor: "#F8FAFC", borderRadius: 1.5 }}
            >
              {STATUS_FILTERS.map((s) => (
                <ToggleButton key={s.value} value={s.value} sx={toggleButtonSx(STATUS_TOGGLE_COLOR[s.value])}>
                  {s.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <MaterialReactTable
            {...sharedTableProps}
            columns={runColumns}
            data={filteredRuns}
            enableExpanding
            renderDetailPanel={({ row }) => <JobRunDetailPanel row={row} />}
            initialState={{
              density: "compact",
              pagination: { pageSize: 25 },
              sorting: [{ id: "StartedTs", desc: true }],
            }}
            muiTableBodyCellProps={{
              sx: {
                borderBottom: "1px solid",
                borderColor: "divider",
                padding: "6px 10px",
                fontSize: "0.8rem",
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                backgroundColor: "#F8FAFC",
                color: "text.secondary",
                fontWeight: 700,
                fontSize: "0.72rem",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                textAlign: "center",
                borderBottom: "2px solid",
                borderColor: "divider",
                whiteSpace: "normal",
                "& .Mui-TableHeadCell-Content": { justifyContent: "center" },
                "& .Mui-TableHeadCell-Content-Wrapper": {
                  whiteSpace: "normal",
                  overflow: "visible",
                  textOverflow: "unset",
                },
              },
            }}
            muiTableContainerProps={{ sx: { maxHeight: "55vh" } }}
          />
        </>
      )}
    </AppDialog>
  );
}

function MetricCard({ label, value, color = "text.primary" }) {
  return (
    <Box sx={{ bgcolor: "#F8FAFC", borderRadius: 1.5, px: 1.5, py: 1.25 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, color }}>
        {value}
      </Typography>
    </Box>
  );
}

function LegendSwatch({ color, label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: color }} />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

// Analytics popup - launched from the main tab's toolbar (see the
// "Analytics" button below), not from any individual job row. Everything
// here is computed from `allRuns` (every collapsed run, unfiltered by the
// main table's own range/status filters) via computeDurationOutliers/
// computeDailyTrend/computeNewCriticalErrors above.
function AnalyticsDialog({ open, onClose, allRuns }) {
  const theme = useTheme();

  // Deliberately NOT gated on `open`: AppDialog's underlying MUI <Dialog>
  // stays mounted through its ~225ms exit transition after `open` flips to
  // false, so gating this on `open` (recomputing to `[]` the instant the
  // close button is clicked) fed the still-mounted BarChart empty axis/
  // series data mid-transition and crashed x-charts' extremum calculation.
  // Computing straight from `allRuns` keeps the props stable across
  // open/close - cheap enough (a handful of array passes over `allRuns`)
  // that there's no real cost to doing it even while the dialog is closed.
  const durationOutliers = useMemo(
    () => computeDurationOutliers(allRuns).slice(0, DURATION_CHART_MAX_ITEMS),
    [allRuns],
  );
  const dailyTrend = useMemo(() => computeDailyTrend(allRuns), [allRuns]);
  const newCriticalErrors = useMemo(() => computeNewCriticalErrors(allRuns), [allRuns]);

  const totals = dailyTrend.reduce(
    (acc, d) => ({
      success: acc.success + d.successCount,
      issues: acc.issues + d.withErrorsCount + d.failedCount,
      total: acc.total + d.successCount + d.withErrorsCount + d.failedCount,
    }),
    { success: 0, issues: 0, total: 0 },
  );
  const successRate = totals.total > 0 ? Math.round((totals.success / totals.total) * 100) : null;

  // Trend direction: the back half of the window's issue count vs the
  // front half's - a simple, explainable "is this getting worse" signal
  // rather than a full regression.
  const half = Math.max(Math.floor(dailyTrend.length / 2), 1);
  const earlierIssues = dailyTrend.slice(0, half).reduce((s, d) => s + d.withErrorsCount + d.failedCount, 0);
  const laterIssues = dailyTrend.slice(-half).reduce((s, d) => s + d.withErrorsCount + d.failedCount, 0);
  const trendDirection =
    laterIssues > earlierIssues * 1.2 ? "rising" : laterIssues < earlierIssues * 0.8 ? "falling" : "steady";
  const trendColor =
    trendDirection === "rising" ? "error.main" : trendDirection === "falling" ? "success.main" : "text.primary";
  const trendLabel = trendDirection === "rising" ? "↑ rising" : trendDirection === "falling" ? "↓ falling" : "steady";

  return (
    <AppDialog open={open} onClose={onClose} title="Analytics — last 7 days" maxWidth="lg" fullWidth>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1.5, mb: 3 }}>
        <MetricCard
          label="Running slower than usual"
          value={durationOutliers.length}
          color={durationOutliers.length > 0 ? "error.main" : "text.primary"}
        />
        <MetricCard
          label="New / critical errors"
          value={newCriticalErrors.length}
          color={newCriticalErrors.length > 0 ? "error.main" : "text.primary"}
        />
        <MetricCard label="Success rate (7d)" value={successRate == null ? "-" : `${successRate}%`} />
        <MetricCard label="Failures, 7d trend" value={trendLabel} color={trendColor} />
      </Box>

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Running longer than usual
      </Typography>
      {durationOutliers.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          No job's latest run is meaningfully longer than its own history right now.
        </Typography>
      ) : (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", gap: 2, mb: 0.5 }}>
            <LegendSwatch color={theme.palette.grey[400]} label="Typical avg" />
            <LegendSwatch color={theme.palette.error.main} label="Latest run" />
          </Box>
          <BarChart
            layout="horizontal"
            height={Math.max(durationOutliers.length * 42 + 60, 150)}
            margin={{ left: 160 }}
            yAxis={[
              {
                scaleType: "band",
                data: durationOutliers.map((o) => o.scheduleId || o.className),
              },
            ]}
            xAxis={[{ label: "minutes" }]}
            series={[
              {
                data: durationOutliers.map((o) => Number(durationMinutes(o.baselineAvgMs).toFixed(1))),
                label: "Typical avg",
                color: theme.palette.grey[400],
                valueFormatter: (v) => `${v} min`,
              },
              {
                data: durationOutliers.map((o) => Number(durationMinutes(o.latestMs).toFixed(1))),
                label: "Latest run",
                color: theme.palette.error.main,
                valueFormatter: (v) => `${v} min`,
              },
            ]}
            slotProps={{ legend: { hidden: true } }}
          />
        </Box>
      )}

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Runs and failures, last {TREND_WINDOW_DAYS} days
      </Typography>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", gap: 2, mb: 0.5 }}>
          <LegendSwatch color={theme.palette.success.main} label="Success" />
          <LegendSwatch color={theme.palette.warning.main} label="With errors" />
          <LegendSwatch color={theme.palette.error.main} label="Failed" />
        </Box>
        <BarChart
          height={220}
          xAxis={[{ scaleType: "band", data: dailyTrend.map((d) => d.label) }]}
          series={[
            { data: dailyTrend.map((d) => d.successCount), label: "Success", stack: "status", color: theme.palette.success.main },
            { data: dailyTrend.map((d) => d.withErrorsCount), label: "With errors", stack: "status", color: theme.palette.warning.main },
            { data: dailyTrend.map((d) => d.failedCount), label: "Failed", stack: "status", color: theme.palette.error.main },
          ]}
          slotProps={{ legend: { hidden: true } }}
        />
      </Box>

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: "flex", alignItems: "center", gap: 0.75 }}>
        <WarningAmberIcon fontSize="small" sx={{ color: "text.secondary" }} />
        New and critical errors
      </Typography>
      {newCriticalErrors.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No new or unusually frequent errors in the last {RECENT_ERROR_WINDOW_HOURS} hours.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {newCriticalErrors.map((e, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "flex-start",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.5,
                px: 1.5,
                py: 1,
              }}
            >
              <StatusChip status={e.status} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2">
                  <Box component="span" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                    {e.className}
                  </Box>
                  {e.scheduleId && e.scheduleId !== e.className ? ` (${e.scheduleId})` : ""} — {e.message}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {e.isNew
                    ? `New - never seen in the prior ${ERROR_BASELINE_WINDOW_DAYS} days`
                    : `Spiking - ${e.count}x in ${RECENT_ERROR_WINDOW_HOURS}h vs an average of about ${(
                        e.baselineCount / ERROR_BASELINE_WINDOW_DAYS
                      ).toFixed(1)}/day`}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </AppDialog>
  );
}

const UnifiedJobRunSummary = () => {
  const { fetchJobRunHistory, jobRunHistory } = useDataView();
  const [selectedClass, setSelectedClass] = useState(null);
  const [rangePreset, setRangePreset] = useState(DEFAULT_RANGE_PRESET);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  // Default ON: a job averaging >50 runs/day is dashboard noise as long as
  // it's healthy - the moment it has a real failure or a SUCCESS_WITH_ERRORS
  // run in the selected window, it reappears regardless of frequency (see
  // isFrequentClean below). Off shows everything, unfiltered, for auditing.
  const [hideFrequentClean, setHideFrequentClean] = useState(true);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  let loading = false;
  // Every run is already recorded the moment it starts (RUNNING) and again
  // on completion, so a short refresh interval is what makes this tab useful
  // as a "what's happening right now" view - the other Monitoring tabs
  // refresh hourly because they're slow-changing daily/weekly summaries.
  const tableMaxHeight = useTableAutoRefresh(fetchJobRunHistory, 60_000);

  if (jobRunHistory.length === 0) {
    loading = true;
  }

  const rangeLabel = rangeLabelFor(rangePreset, customStart, customEnd);
  const classColumns = useMemo(() => getClassSummaryColumns(rangeLabel), [rangeLabel]);

  const allRuns = useMemo(() => collapseRunsByStartedTs(jobRunHistory), [jobRunHistory]);

  const { start: rangeStart, end: rangeEnd } = useMemo(
    () => getRangeBounds(rangePreset, customStart, customEnd),
    [rangePreset, customStart, customEnd],
  );

  // Date range narrows which runs get aggregated at all; status filter only
  // narrows which class *rows* are shown afterward (so e.g. "Failed" surfaces
  // every class with a failure in the window, while still showing that
  // class's full success/fail counts for context, not just the failures).
  const runsInRange = useMemo(
    () =>
      allRuns.filter((r) => {
        const started = parseUtcTimestamp(r.StartedTs);
        if (!started) return false;
        return started >= rangeStart && started <= rangeEnd;
      }),
    [allRuns, rangeStart, rangeEnd],
  );

  const rawClassSummaries = useMemo(() => summarizeByClass(runsInRange), [runsInRange]);

  // How many days the current selection actually spans, for a "runs/day"
  // rate rather than a raw count - so the >50/day threshold means the same
  // thing whether you're looking at 24h, 30d, or a custom range.
  const rangeDays = Math.max((rangeEnd.getTime() - rangeStart.getTime()) / (24 * 60 * 60 * 1000), 1);

  const classSummaries = useMemo(
    () =>
      rawClassSummaries.map((c) => {
        const runsPerDay = c.totalRuns / rangeDays;
        const isClean = c.failedCount === 0 && c.withErrorsCount === 0;
        return {
          ...c,
          runsPerDay,
          isFrequentClean: runsPerDay > FREQUENT_RUNS_PER_DAY_THRESHOLD && isClean,
        };
      }),
    [rawClassSummaries, rangeDays],
  );

  const hiddenFrequentCleanCount = classSummaries.filter((c) => c.isFrequentClean).length;

  const visibleClassSummaries = useMemo(() => {
    let rows = classSummaries;
    if (hideFrequentClean) rows = rows.filter((c) => !c.isFrequentClean);
    if (statusFilter === "ALL") return rows;
    return rows.filter((c) => {
      if (statusFilter === "SUCCESS") return c.successCount > 0;
      if (statusFilter === "SUCCESS_WITH_ERRORS") return c.withErrorsCount > 0;
      if (statusFilter === "FAILED") return c.failedCount > 0;
      if (statusFilter === "RUNNING") return c.runningCount > 0;
      return true;
    });
  }, [classSummaries, hideFrequentClean, statusFilter]);

  const selectedClassRuns = useMemo(() => {
    if (!selectedClass) return [];
    return runsInRange
      .filter((r) => r.ClassName === selectedClass.ClassName && r.ScheduleId === selectedClass.ScheduleId)
      .sort(
        (a, b) => (parseUtcTimestamp(b.StartedTs)?.getTime() || 0) - (parseUtcTimestamp(a.StartedTs)?.getTime() || 0),
      );
  }, [runsInRange, selectedClass]);

  const today = new Date().toDateString();
  const runningCount = runsInRange.filter((row) => row.Status === "RUNNING").length;
  const failedTodayCount = runsInRange.filter((row) => {
    const started = parseUtcTimestamp(row.StartedTs);
    return row.Status === "FAILED" && started && started.toDateString() === today;
  }).length;
  const successTodayCount = runsInRange.filter((row) => {
    const started = parseUtcTimestamp(row.StartedTs);
    return row.Status === "SUCCESS" && started && started.toDateString() === today;
  }).length;

  const lastRefreshedTime = jobRunHistory.reduce((latest, entry) => {
    const candidate = parseUtcTimestamp(entry?.InsertDate);
    if (!candidate) return latest;
    return !latest || candidate > latest ? candidate : latest;
  }, null);

  // Same UTC-aware parse as the Last Run/Started/Completed columns above -
  // previously this line only relabeled the raw UTC digits with a local
  // zone abbreviation rather than actually converting them, the same bug
  // reported for Last Run.
  const localDateString = lastRefreshedTime
    ? lastRefreshedTime.toLocaleString("en-US", {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timeZoneName: "short",
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : "N/A";

  if (Boolean(loading)) {
    return <ShowLoadingBar title="Cron Jobs" loading={loading} />;
  }

  return (
    <div className="relative">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2.5,
          px: 2,
          py: 1.25,
          mb: 1.5,
          bgcolor: "#F8FAFC",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Typography
            variant="caption"
            sx={{ textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700, color: "primary.main" }}
          >
            Range
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={rangePreset}
            onChange={(e, val) => val && setRangePreset(val)}
            sx={{ bgcolor: "background.paper", borderRadius: 1.5 }}
          >
            {RANGE_PRESETS.map((p) => (
              <ToggleButton key={p.value} value={p.value} sx={toggleButtonSx("primary")}>
                {p.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {rangePreset === "custom" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                type="date"
                size="small"
                label="From"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150, bgcolor: "background.paper" }}
              />
              <TextField
                type="date"
                size="small"
                label="To"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150, bgcolor: "background.paper" }}
              />
            </Box>
          )}
        </Box>

        <Box sx={{ width: "1px", height: 24, bgcolor: "divider" }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Typography
            variant="caption"
            sx={{ textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700, color: "primary.main" }}
          >
            Status
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={statusFilter}
            onChange={(e, val) => val && setStatusFilter(val)}
            sx={{ bgcolor: "background.paper", borderRadius: 1.5 }}
          >
            {STATUS_FILTERS.map((s) => (
              <ToggleButton key={s.value} value={s.value} sx={toggleButtonSx(STATUS_TOGGLE_COLOR[s.value])}>
                {s.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ width: "1px", height: 24, bgcolor: "divider" }} />

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={hideFrequentClean}
              onChange={(e) => setHideFrequentClean(e.target.checked)}
            />
          }
          label={
            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
              {`Hide frequent, clean jobs (>${FREQUENT_RUNS_PER_DAY_THRESHOLD}/day)`}
              {hiddenFrequentCleanCount > 0 && (
                <Box component="span" sx={{ color: "primary.main", fontWeight: 700 }}>
                  {` (${hiddenFrequentCleanCount} hidden)`}
                </Box>
              )}
            </Typography>
          }
          sx={{ ml: 0 }}
        />

        <Box sx={{ ml: "auto" }}>
          <RangeLegend />
        </Box>
      </Box>

      {classSummaries.length > 0 && (
        <MaterialReactTable
          {...sharedTableProps}
          columns={classColumns}
          data={visibleClassSummaries}
          enablePinning
          initialState={{
            density: "compact",
            pagination: { pageSize: 100 },
            sorting: [{ id: "ScheduleId", desc: false }],
            columnPinning: { left: ["ScheduleId"] },
          }}
          autoResetPageIndex={false}
          renderTopToolbarCustomActions={() => (
            <ToolbarRow
              actions={
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<InsertChartOutlinedIcon fontSize="small" />}
                  onClick={() => setAnalyticsOpen(true)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    mr: 3.5,
                    bgcolor: "#00ACC1",
                    "&:hover": { bgcolor: "#008BA3" },
                  }}
                >
                  Analytics
                </Button>
              }
            >
              <ToolbarStat label="Last Refreshed" value={localDateString} color="primary.main" />
              <ToolbarStat label="Classes" value={visibleClassSummaries.length} color="text.primary" />
              <ToolbarStat
                label="Running"
                value={runningCount}
                color={runningCount > 0 ? "info.main" : "text.primary"}
              />
              <ToolbarStat label="Succeeded Today" value={successTodayCount} color="success.main" />
              <ToolbarStat
                label="Failed Today"
                value={failedTodayCount}
                color={failedTodayCount > 0 ? "error.main" : "success.main"}
              />
            </ToolbarRow>
          )}
          muiTopToolbarProps={{ sx: { mt: 3 } }}
          muiTableBodyCellProps={({ column }) => ({
            sx: {
              borderBottom: "1px solid",
              borderColor: "divider",
              padding: "6px 10px",
              fontSize: "0.8rem",
              ...(column.id === "ScheduleId" && {
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.3,
              }),
            },
          })}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: "#F8FAFC",
              color: "text.secondary",
              fontWeight: 700,
              fontSize: "0.72rem",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              textAlign: "center",
              borderBottom: "2px solid",
              borderColor: "divider",
              "& .Mui-TableHeadCell-Content": { justifyContent: "center" },
              "& .Mui-TableHeadCell-Content-Wrapper": {
                whiteSpace: "normal",
                overflow: "visible",
              },
            },
          }}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () => setSelectedClass(row.original),
            sx: {
              cursor: "pointer",
              "&:nth-of-type(even) td": { backgroundColor: "#FAFBFC" },
              "&:hover td": { backgroundColor: "action.hover" },
            },
          })}
          muiTableContainerProps={{ sx: { maxHeight: tableMaxHeight } }}
          muiTableHeadProps={{
            sx: {
              position: "sticky",
              top: 0,
              zIndex: 2,
              backgroundColor: "background.paper",
            },
          }}
        />
      )}

      <ClassRunsDialog
        open={!!selectedClass}
        onClose={() => setSelectedClass(null)}
        classSummary={selectedClass}
        runs={selectedClassRuns}
        rangeLabel={rangeLabel}
        initialStatusFilter={statusFilter}
      />

      <AnalyticsDialog open={analyticsOpen} onClose={() => setAnalyticsOpen(false)} allRuns={allRuns} />
    </div>
  );
};

export default UnifiedJobRunSummary;
